import React, { useState, useEffect } from "react";
import Cropper from "react-easy-crop";
import { IoMdClose } from "react-icons/io";
import Loader from "../../assets/img/loader.gif";

const CroppedImage = ({
  handleMapClose,
  initialImage,
  setInitialImage,
  setSelectedImage,
  setCroppedImage,
  onAspectRatioSelect,
  selectedScale,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const aspectRatioOptions = [
    { label: "1:1", value: 1, scale: "1:1" },
    { label: "4:5", value: 4 / 5, scale: "4:5" },
    { label: "16:9", value: 16 / 9, scale: "16:9" },
    { label: "9:16", value: 9 / 16, scale: "9:16" },
  ];

  // Initialize aspect ratio based on selectedScale
  const getInitialAspectRatio = () => {
    const option = aspectRatioOptions.find(opt => opt.scale === selectedScale);
    return option ? option.value : 16 / 9; // Default to 16:9 if not found
  };

  const [aspectRatio, setAspectRatio] = useState(getInitialAspectRatio());

  // Update aspect ratio when selectedScale changes
  useEffect(() => {
    const newAspectRatio = getInitialAspectRatio();
    setAspectRatio(newAspectRatio);
  }, [selectedScale]);

  const onCropComplete = (area, areaPixels) => {
    setCroppedAreaPixels(areaPixels); // Just store it
  };

  const handleAspectRatioChange = (ratio) => {
    setAspectRatio(ratio);
    // Reset crop position when changing aspect ratio
    setCrop({ x: 0, y: 0 });
    
    // Immediately update the scale when user selects an aspect ratio
    const selectedOption = aspectRatioOptions.find(option => option.value === ratio);
    if (selectedOption && onAspectRatioSelect) {
      console.log("Immediately updating scale to:", selectedOption.scale);
      onAspectRatioSelect(selectedOption.scale);
    }
  };

  const handleCrop = async () => {
    setLoading(true);
    try {
      const croppedFile = await getCroppedImg(
        URL.createObjectURL(initialImage),
        croppedAreaPixels
      );
      setCroppedImage(croppedFile);
      
      // Get the selected scale value
      const selectedOption = aspectRatioOptions.find(option => option.value === aspectRatio);
      console.log("Current aspectRatio:", aspectRatio);
      console.log("Selected option:", selectedOption);
      if (selectedOption && onAspectRatioSelect) {
        console.log("Calling onAspectRatioSelect with scale:", selectedOption.scale);
        onAspectRatioSelect(selectedOption.scale);
      }
      
      handleMapClose();
    } catch (error) {
      console.error("Cropping failed:", error);
    } finally {
      setLoading(false); // Stop loader
    }
  };
  const getCroppedImg = (imageSrc, crop) => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.src = imageSrc;

      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(
          image,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          0,
          0,
          crop.width,
          crop.height
        );

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }

          // 🔥 Convert blob to File object
          const file = new File([blob], `cropped_${Date.now()}.jpg`, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });

          resolve(file); // 👈 Now it's a File object
        }, "image/jpeg");
      };

      image.onerror = (error) => reject(error);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div className="max-h-[90vh] w-[700px] overflow-auto rounded-xl bg-navy-800 px-6 pb-6 shadow-lg">
        <div className="relative flex justify-center py-4">
          <p className="text-2xl font-semibold">Crop Image</p>
          <IoMdClose
            className="absolute right-0 cursor-pointer text-2xl"
            onClick={handleMapClose}
          />
        </div>

        <div className="flex justify-center">
          <div className="relative h-[500px] w-[500px]">
            <Cropper
              image={URL.createObjectURL(initialImage)}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>
        </div>

        {/* Aspect Ratio Selection */}
        <div className="mt-4">
          <p className="mb-3 text-center text-sm font-medium text-gray-300">
            Select Aspect Ratio
          </p>
          <div className="flex justify-center gap-3">
            {aspectRatioOptions.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => handleAspectRatioChange(option.value)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  aspectRatio === option.value
                    ? "bg-primary text-white"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <button
            onClick={handleCrop}
            type="button"
            className="h-[40px] w-[134.17px] rounded bg-primary px-6 py-2 text-white hover:bg-primary/80"
            disabled={loading}
          >
            {loading ? (
              <>
                <img
                  src={Loader}
                  alt="Loading..."
                  className="mx-auto h-6 w-6"
                />
              </>
            ) : (
              "Crop & Save"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CroppedImage;
